import React, { useState, useEffect } from 'react';
import { useSiteSettings } from '../App';
import { useNavigate } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import axios from 'axios';
import NotificationBell from './NotificationBell';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const StickyHeader = ({ user }) => {
  const { siteSettings } = useSiteSettings();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const showMessageIcon = user && ((user.role === 'admin' || user.role === 'supervisor') || user.role === 'employee'); // Only for admins/employees, not clients

  useEffect(() => {
    if (showMessageIcon) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [showMessageIcon]);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/direct-messages/unread/count`);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch unread messages count');
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 shadow-md" style={{ position: 'fixed', background: 'var(--nav-bg, #2563eb)' }}>
      <div className="w-full px-3 sm:px-4 py-2 sm:py-3">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          {/* Logo */}
          <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0 justify-center md:justify-start">
            {siteSettings.site_logo ? (
              <div className="bg-white rounded-md sm:rounded-lg p-1.5 sm:p-2 shadow-sm flex items-center justify-center flex-shrink-0">
                <img 
                  src={siteSettings.site_logo.startsWith('http') || siteSettings.site_logo.startsWith('/') ? siteSettings.site_logo : `${BACKEND_URL}${siteSettings.site_logo}`}
                  alt={siteSettings.site_title}
                  className="h-7 sm:h-10 w-auto object-contain max-w-[60px] sm:max-w-none"
                  onError={(e) => { console.error('Logo failed to load:', e); }}
                />
              </div>
            ) : (
              <div className="h-8 sm:h-12 w-8 sm:w-12 bg-white rounded-md sm:rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                <span className="text-blue-600 font-bold text-lg sm:text-2xl">
                  {siteSettings.site_title?.charAt(0) || 'B'}
                </span>
              </div>
            )}

            {/* Title - Only show if custom title is set and different from default */}
            {siteSettings.site_title && siteSettings.site_title !== 'BuildTrack' && (
              <div className="text-center md:text-left flex-1 min-w-0">
                <h1 className="text-base sm:text-2xl font-bold text-white truncate px-1">
                  {siteSettings.site_title}
                </h1>
                {siteSettings.site_subtitle && siteSettings.site_subtitle !== 'Construction Project Management' && (
                  <p className="text-xs sm:text-sm text-blue-100 hidden sm:block truncate px-1">{siteSettings.site_subtitle}</p>
                )}
              </div>
            )}
          </div>

          {/* Notifications and Messages - Top Right */}
          <div className="flex items-center gap-2">
            {/* Notification Bell for Clients */}
            <NotificationBell user={user} />
            
            {/* Message Icon for Admins/Employees */}
            {showMessageIcon && (
              <button
                onClick={() => navigate('/messages')}
                className="relative p-2 hover:bg-blue-700 rounded-lg transition-colors flex-shrink-0"
                aria-label="Messages"
                data-testid="message-icon-button"
              >
                <MessageSquare className="w-6 h-6 text-white" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-600 rounded-full shadow-md animate-pulse">
                    {unreadCount}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StickyHeader;
